import Axios from "axios";
import { PublicationContext } from "common/context/Publication";
import { publicationTypes } from "common/context/Publication/PublicationContext";
import Skin from "Components/Skin";
import { FC, useCallback, useContext, useEffect, useState } from "react";
import Frame from "../Frame";
import { LayoutProps } from "../types/Layout.types";
import { Wrapper } from "./Article.styled";
import ArticleBody from "./ArticleBody";
import SwipeOverlay from "./components/SwipeOverlay";
import { ArticleContextProvider } from "./Context";
import Header from "./Header";
import Meta from "./Meta";
import { ArticleProps } from "./types/Article.types";

interface Props {
  article: ArticleProps;
  menus: LayoutProps;
}

const Article: FC<Props> = ({ article, menus }) => {
  const { publication } = useContext<publicationTypes>(PublicationContext);
  const [comments, setComments] = useState<any[]>([]);
  const [commentCount, setCommentCount] = useState<number | undefined>(
    undefined
  );

  const fetchComments = useCallback(async (id: string) => {
    try {
      const response = await Axios.get(`/api/comment/get?id=${id}`);
      if (response.status === 200) {
        const allComments = response.data;
        // Include comments that have no 'users' array or fewer than 3 users
        const filteredComments = allComments.filter(
          (comment: any) =>
            !Array.isArray(comment.users) || comment.users.length < 3
        );

        setComments(allComments);
        setCommentCount(filteredComments.length);
      }
    } catch (err) {}
  }, []);

  // Add comment logic to update state and re-fetch
  const addComment = useCallback(
    async (comment: any, id: string) => {
      try {
        await Axios.post("/api/comment/post", comment);
        fetchComments(id);
      } catch (err) {
        console.log({ err });
      }
    },
    [fetchComments]
  );

  // Fetch comments on mount if article id exists
  useEffect(() => {
    if (article?.data?.id) {
      fetchComments(article.data.id);
    }
  }, [article?.data?.id, fetchComments]);

  if (!article) {
    return null;
  }

  return (
    <Frame
      data={menus}
      title={article?.data.title}
      canonicalUrl={
        article?.data.extra?.canonicalUrl
          ? `https://www.${publication}${article?.data.extra?.canonicalUrl}`
          : `https://www.${publication}${article?.data.path}`
      }
      favicon="/favicons/tindle_crest.ico"
    >
      <ArticleContextProvider publication={publication} article={article}>
        <Skin id={`wp`} />
        <Meta />
        <Wrapper>
          <Header commentCount={commentCount} />
          <ArticleBody
            comments={comments}
            commentCount={commentCount}
            setComments={setComments}
            addComment={addComment}
            articleId={article?.data?.id}
          />
          <SwipeOverlay />
        </Wrapper>
      </ArticleContextProvider>
    </Frame>
  );
};

export default Article;
